import { type ApolloClient } from 'apollo-client';
import type { DocumentNode } from 'graphql';
import gql from 'graphql-tag';
import { fg } from '@atlaskit/platform-feature-flags';
import { graphql, fetchQuery, type Environment } from 'react-relay';
import type { getIsRovoEnabledQuery } from './__generated__/getIsRovoEnabledQuery.graphql';
import { createAtlAttributionHeader } from '../../utils';

type AtlassianStudioUserSiteContextOutput = {
	isRovoEnabled: boolean | null;
};

type GetIsRovoEnabledResponse = {
	atlassianStudio_userSiteContext: AtlassianStudioUserSiteContextOutput | null;
};

export const GET_IS_ROVO_ENABLED: DocumentNode = gql`
	query forgeGetIsRovoEnabled($cloudId: ID!) {
		atlassianStudio_userSiteContext(cloudId: $cloudId) {
			... on AtlassianStudioUserSiteContextOutput {
				isRovoEnabled
			}
		}
	}
`;

export const getIsRovoEnabled = async ({
	client,
	cloudId,
	relayEnvironment,
}: {
	client: ApolloClient<object>;
	cloudId: string;
	relayEnvironment?: Environment;
}): Promise<boolean> => {
	try {
		if (relayEnvironment) {
			// eslint-disable-next-line @atlaskit/platform/no-preconditioning
			if (fg('forge-ui-add-relay-support') && fg('forge-ui-add-relay-get-rovo-enabled-query')) {
				const observable = fetchQuery<getIsRovoEnabledQuery>(
					relayEnvironment,
					graphql`
						query getIsRovoEnabledQuery($cloudId: ID!) {
							atlassianStudio_userSiteContext(cloudId: $cloudId) {
								... on AtlassianStudioUserSiteContextOutput {
									isRovoEnabled
								}
							}
						}
					`,
					{ cloudId },
					{
						networkCacheConfig: {
							metadata: {
								headers: {
									'atl-attribution': createAtlAttributionHeader(),
								}
							},
						},
					}
				);

				return new Promise<boolean>((resolve) => {
					observable.subscribe({
						next: (data) => {
							resolve(data?.atlassianStudio_userSiteContext?.isRovoEnabled ?? false);
						},
						error: () => {
							resolve(false);
						},
					});
				});
			}
		}

		const response = await client.query<GetIsRovoEnabledResponse>({
			query: GET_IS_ROVO_ENABLED,
			variables: { cloudId },
		});

		return response.data?.atlassianStudio_userSiteContext?.isRovoEnabled ?? false;
		// eslint-disable-next-line no-unused-vars
	} catch (err) {
		return false;
	}
};
